import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  TrendingUp,
  Shield,
  ClipboardCheck,
  Zap,
  Building2,
  Heart,
  BarChart3,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";
import SectionDivider from "../components/SectionDivider";

const WorkforceDevelopment = () => {
  const outcomes = [
    "Higher staff retention and lower turnover costs",
    "Measurable improvements in daily productivity",
    "Clearer roles, expectations, and accountability across your team",
    "Stronger leadership from managers and team leads",
    "Reduced burnout and improved employee satisfaction",
    "Consistent performance standards your team actually follows",
    "Operational workflows your team can sustain without you",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Private practices with growing teams" },
    { icon: Heart, label: "Outpatient clinics experiencing staff turnover" },
    { icon: Users, label: "Home healthcare agencies struggling with accountability" },
    { icon: TrendingUp, label: "Healthcare startups building their first team" },
  ];

  return (
    <div>
      <SEO
        title="Workforce Development & Staff Productivity for Healthcare Practices"
        description="Struggling with staff burnout, high turnover, or lack of accountability in your clinic? RNB Consulting Group helps healthcare practices build productive, accountable teams through hands-on workforce development and operational training."
        keywords="improve staff productivity clinic, healthcare employee management, reduce staff burnout medical practice, team accountability healthcare, clinic workforce development, manage healthcare employees, staff performance improvement clinic"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Workforce Development", "item": "https://www.rnbconsultinggroup.com/services/workforce-development" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <Users className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Workforce Development for Healthcare</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="workforce-hero-headline">
                Your Team Should Be Your Biggest Asset
                <span className="block mt-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  Not Your Biggest Headache
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                We help clinic owners and healthcare leaders build productive, accountable teams — through hands-on training, leadership development, and systems that make expectations clear and follow-through easy.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Reduce staff turnover</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Build real accountability</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Improve daily productivity</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="workforce-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/team-tablet-training.jpg"
                alt="Healthcare team collaborating on tablet during training session"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Acknowledgment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Sound Familiar?
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              You're not alone. These are the most common workforce challenges we hear from practice owners every week.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "Staff shows up — but the effort and initiative just aren't there",
              "You've tried performance reviews, but nothing changes",
              "You're losing good people to burnout, and replacing them is expensive",
              "Managers and team leads aren't stepping up the way you need",
              "New hires take forever to ramp up — and some never do",
              "You're the only one holding anyone accountable, and you're exhausted",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-500 text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              How We Help Your Team Perform Better
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              We don't just talk about what needs to change — we work with you and your team to make it happen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Target, title: "Accountability Systems", desc: "We help you put clear performance expectations, check-ins, and follow-through processes in place — so your team knows exactly what's expected." },
              { icon: Users, title: "Leadership Development", desc: "Your managers and team leads get hands-on coaching to lead effectively — not just supervise — so you're not the only one keeping things on track." },
              { icon: ClipboardCheck, title: "Onboarding & Training Programs", desc: "We design onboarding workflows and training programs that get new hires contributing faster and set them up for long-term success." },
              { icon: TrendingUp, title: "Performance Improvement Plans", desc: "When performance slips, we help you address it constructively with structured plans that are fair, clear, and documented." },
              { icon: Zap, title: "Workflow & Role Clarity", desc: "We map out who does what, eliminate overlaps and gaps, and give every team member a clear lane — reducing confusion and conflict." },
              { icon: Shield, title: "Retention & Culture Building", desc: "We help you identify what's driving people away and build a work culture that staff want to stay in — beyond just pay raises." },
            ].map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={1} image="/images/healthcare/staff-meeting-huddle.jpg" />

      {/* Outcomes */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What You Can Expect
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Practices we've worked with consistently see these results — and they're sustainable because your team owns the changes.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-rnb-gray mt-8 text-sm">
            <strong className="text-rnb-navy">Important:</strong> We are not a staffing agency or recruiting firm. We help you develop and optimize the team you already have.
          </p>
        </div>
      </section>

      {/* Why RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Healthcare Practices Choose RNB
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Heart, title: "Healthcare-Only Focus", desc: "We work exclusively with healthcare organizations — so we understand the pressures, regulations, and staffing realities you deal with every day." },
              { icon: ClipboardCheck, title: "Implementation, Not Just Advice", desc: "We don't hand you a report and walk away. We work alongside your team to put new systems in place and make sure they stick." },
              { icon: BarChart3, title: "Scalable Systems", desc: "Everything we build with you is designed to grow with your practice — whether you're adding staff, locations, or services." },
              { icon: Users, title: "Training & Long-Term Support", desc: "We train your team to own the changes, so improvements continue long after our engagement ends." },
            ].map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-start gap-4 p-5">
                <div className="w-10 h-10 bg-rnb-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-teal" />
                </div>
                <div>
                  <h3 className="font-bold text-rnb-navy mb-1">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Your Team Problems Won't Fix Themselves
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            The longer you wait, the more it costs — in turnover, lost productivity, and your own energy. Let's have an honest conversation about what's going on and how we can help.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="workforce-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkforceDevelopment;
